import {
  boardPath,
  boardSchema,
  onboardingTipsSchema,
  welcomeBoardProvisionResultSchema,
  type ApiError,
  type Board,
  type OnboardingTips,
  type WelcomeBoardProvisionResult,
} from '@molt/shared';
import {
  queryOptions,
  useMutation,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';
import { get, post } from './api';
import { boardContentsQueryOptions } from './board-face';
import { queryKeys } from './query-keys';

export function provisionWelcomeBoard(): Promise<WelcomeBoardProvisionResult> {
  return post('/me/onboarding/welcome-board', welcomeBoardProvisionResultSchema, {});
}

export function getBoardHeader(boardId: string): Promise<Board> {
  return get(`/boards/${encodeURIComponent(boardId)}`, boardSchema);
}

export async function onboardingBoardPath(boardId: string): Promise<string> {
  const board = await getBoardHeader(boardId);
  return boardPath(board.shortLink, board.slug);
}

export function invalidateOnboardingLanding(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: queryKeys.workspaces.all() });
  void queryClient.invalidateQueries({ queryKey: queryKeys.boards.pages() });
}

export function useProvisionWelcomeBoard(): UseMutationResult<
  WelcomeBoardProvisionResult,
  ApiError,
  void
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: queryKeys.meOnboardingWelcomeBoard(),
    mutationFn: provisionWelcomeBoard,
    onSuccess: (result) => {
      if (result.status === 'created' || result.status === 'existing') {
        invalidateOnboardingLanding(queryClient);
      }
    },
  });
}

export function useOnboardingBoardPath(): UseMutationResult<string, ApiError, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (boardId) => {
      const contents = await queryClient.fetchQuery(boardContentsQueryOptions(boardId));
      return boardPath(contents.board.shortLink, contents.board.slug);
    },
  });
}

/**
 * ── The one-time first-run tip — `FR-EXP-033` AC2 ──────────────────────────
 *
 * *"when the user drags the first card to the next list, then a confirmation
 * of the interaction is shown and **the tip does not repeat**."*
 *
 * `GET /me/onboarding/tips` answers both halves the board face needs: whether
 * this account has already had its one tip, and which board (if any) is this
 * member's welcome board. The second is why `boardSchema` did not have to be
 * widened — the board face compares `board.id` against `welcomeBoardId`
 * instead of reading a flag off the board, so no other board response changed.
 *
 * ⚠ **One-time means once per account, ever.** There is no un-see verb here
 * because there is none in the contract: the route is a `POST` with an empty
 * strict body, and the server sets `has_seen_welcome_tip` to true
 * unconditionally. The tip is not "dismissible"; it is spent.
 */

export function getOnboardingTips(): Promise<OnboardingTips> {
  return get('/me/onboarding/tips', onboardingTipsSchema);
}

export function markWelcomeTipSeen(): Promise<OnboardingTips> {
  return post('/me/onboarding/tips/welcome-tip/seen', onboardingTipsSchema, {});
}

/**
 * Gated on a session: an anonymous board viewer has no account to have a
 * first run of, and the route is 401 for them.
 */
export function onboardingTipsQueryOptions(signedIn: boolean) {
  return queryOptions({
    queryKey: queryKeys.meOnboardingTips(),
    queryFn: getOnboardingTips,
    enabled: signedIn,
  });
}

/**
 * Optimistic in one direction only, which is the whole shape of the feature:
 * the tip is spent the moment the member does the thing it asked for, and it
 * must not flicker back if the acknowledgement is slow.
 *
 * ⚠ **No `onError` rollback, deliberately.** Restoring `welcomeTipSeen: false`
 * on a failed POST would put the tip back on screen *after* the member had
 * already dragged the card — the one thing `FR-EXP-033` AC2 forbids. A tip
 * that stays hidden through a failed write costs a brand-new account nothing;
 * one that reappears reads as the product not having noticed. The flag is
 * re-read from the server on the next mount, so a genuinely failed write means
 * the tip returns on the next visit, not mid-interaction.
 */
export function useMarkWelcomeTipSeen(): UseMutationResult<
  OnboardingTips,
  ApiError,
  void
> {
  const queryClient = useQueryClient();
  const key = queryKeys.meOnboardingTips();

  return useMutation<OnboardingTips, ApiError, void>({
    mutationFn: markWelcomeTipSeen,
    onMutate: async () => {
      /*
       * ⚠ **Cancel before the optimistic write**, and this is the one race the
       * no-rollback rule above does not by itself cover.
       *
       * `onboardingTipsQueryOptions` sets no `staleTime`, so a mount or a
       * window focus can leave a `GET /me/onboarding/tips` in flight at the
       * moment the member drags the first card. That read was issued *before*
       * the acknowledgement and truthfully answers `welcomeTipSeen: false`;
       * resolving after the optimistic flip, it writes the spent tip straight
       * back into the cache and the tip returns — the single outcome
       * `FR-EXP-033` AC2 forbids, arriving with nothing red anywhere and only
       * on a slow connection. Cancelling first is what makes "the tip does not
       * repeat" hold against the clock as well as against a failed write.
       */
      await queryClient.cancelQueries({ queryKey: key });

      const previous = queryClient.getQueryData<OnboardingTips>(key);
      if (previous !== undefined) {
        queryClient.setQueryData<OnboardingTips>(key, {
          ...previous,
          welcomeTipSeen: true,
        });
      }
    },
    onSuccess: (tips) => {
      queryClient.setQueryData<OnboardingTips>(key, tips);
    },
  });
}
